import React from 'react';
import { ProductModelProps } from '@typings/product';
import noop from 'lodash/noop';
import Product from '@models/product';
import { AssetProps } from '@repo/ui/src/types/image';
import { logProduct } from '@utils/productWrapper';
import useAddToBasket from '@hooks/basket/useAddToBasket';
import useShopDataProducts from '@hooks/product/useShopDataProducts';

export interface AddOnProps {
  label: string;
  addons: Array<{
    overlay_image?: AssetProps;
    sku: string;
  }>;
}

interface ContextProps {
  toggleProduct: (sku: string) => void;
  selectedSkus: Array<string>;
  selectedProducts: Array<Product>;
  productList: Array<Product>;
  totalPrice: string;
  originalPrice: string;
  addToCart: (quantity: number) => void;
  addOnProductGroups: Array<{
    label: string;
    addons: Array<Product>;
  }>;
  isLoading: boolean;
  isLoadingAddToCart: boolean;
  mainProduct: Product | null;
  locale: string;
}

export const ProductContext = React.createContext<ContextProps>({
  toggleProduct: noop,
  productList: [],
  selectedSkus: [],
  selectedProducts: [],
  totalPrice: '',
  originalPrice: '',
  addToCart: noop,
  addOnProductGroups: [],
  isLoading: false,
  isLoadingAddToCart: false,
  mainProduct: null,
  locale: '',
});

interface Props {
  children: React.ReactNode;
  product: Product;
  altProduct?: ProductModelProps;
  addOns?: Array<AddOnProps>;
  locale: string;
}

export const ProductProvider = ({
  children,
  product,
  altProduct,
  addOns: addOnGroups,
  locale,
}: Props) => {
  const grills = React.useMemo(
    () => (altProduct ? [product, new Product(altProduct)] : [product]),
    [product, altProduct],
  );
  const grillSkus = React.useMemo(() => grills.map((grill) => grill.sku), [grills]);
  const addOns = React.useMemo(
    () =>
      addOnGroups
        ?.map((addOn) =>
          addOn.addons.map((a) => ({
            ...a,
            label: addOn.label,
          })),
        )
        ?.flat() ?? [],
    [addOnGroups],
  );
  const addOnSkus = addOns.map((a) => a.sku);
  const { data: shopDataProducts, isLoading: isShopProductListLoading } = useShopDataProducts(
    [...grillSkus, ...addOnSkus].filter(Boolean),
    true,
  );

  const { trigger, isMutating: isLoadingAddToCart } = useAddToBasket();
  const [selectedSkus, setSelectedSkus] = React.useState<Array<string>>([product.sku]);

  const allProducts = React.useMemo(() => {
    const filteredShopDataProducts = shopDataProducts?.filter((product) => product?.id);

    if (!filteredShopDataProducts?.length) {
      return grills;
    }

    return filteredShopDataProducts?.map((shop) => {
      if (shop.id === product?.sku) {
        product.setShop(shop);
        return product;
      }

      const isGrill = grillSkus.includes(shop.id);
      const extraData = isGrill ? altProduct : { addOn: addOns.find(({ sku }) => sku === shop.id) };

      // @ts-expect-error addons are missing full product data
      return new Product({ shop, ...extraData });
    });
  }, [addOns, altProduct, grills, grillSkus, product, shopDataProducts]);

  const toggleProduct = React.useCallback(
    (sku: string) => {
      setSelectedSkus((curr) => {
        if (curr.includes(sku)) {
          return curr.filter((item) => item !== sku);
        }
        const productIndex = allProducts.findIndex((item) => item.sku === sku);
        logProduct('PRODUCT_CLICKED', {
          product: allProducts[productIndex],
          list_name: 'Addon Product',
          locale,
          position: productIndex,
          coupon_name: allProducts[productIndex]?.promotionId,
        });
        return [...curr, sku];
      });
    },
    [allProducts, locale],
  );

  const selectedProducts = React.useMemo(
    () => allProducts.filter((p) => selectedSkus.includes(p.sku)),
    [allProducts, selectedSkus],
  );

  const productList = React.useMemo(
    () =>
      allProducts.map((item) => {
        item.checked = selectedSkus.includes(item.sku);
        return item;
      }),
    [allProducts, selectedSkus],
  );
  const addOnProductGroups = React.useMemo(
    () =>
      addOnGroups?.map((group) => ({
        ...group,
        addons: group.addons
          .map(({ sku }) => allProducts.find((item) => item.sku === sku))
          .filter<Product>((addOn) => addOn != null),
      })) ?? [],
    [addOnGroups, allProducts],
  );

  const addToCart = React.useCallback(
    (quantity = 1) =>
      trigger(
        selectedProducts.map((p) => {
          p.quantity = quantity;
          return p;
        }),
      ),
    [selectedProducts, trigger],
  );

  const { totalPrice, originalPrice } = React.useMemo(
    () => ({
      totalPrice: selectedProducts.reduce((sum, p) => sum + p.price, 0).toFixed(2),
      originalPrice: selectedProducts.reduce((sum, p) => sum + p.originalPrice, 0).toFixed(2),
    }),
    [selectedProducts],
  );

  return (
    <ProductContext.Provider
      value={{
        addToCart,
        addOnProductGroups,
        isLoading: isShopProductListLoading,
        selectedSkus,
        selectedProducts,
        productList,
        toggleProduct,
        totalPrice,
        originalPrice,
        isLoadingAddToCart,
        mainProduct: product,
        locale,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
